import React, { useState } from 'react';
import inventoryLedgerService from '../../services/inventoryLedgerService';
import { useStore } from '../../context/StoreContext';

const ProductImport = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const { activeStore: store } = useStore();

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import.');
      return;
    }
    if (!store) {
      setError('Please select a store first.');
      return;
    }

    setImporting(true);
    setError(null);
    setReport(null);

    try {
      console.log('File to send:', file);
      const response = await inventoryLedgerService.importProducts(file, store._id);
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred during import.');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ['Product Name', 'SKU', 'Barcode', 'Category', 'Subcategory', 'Retail Price', 'Cost Price', 'Stock Quantity', 'Description'];
    const csvContent = "data:text/csv;charset=utf-8," + headers.join(",");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "product_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Import Products from CSV</h2>
      <p className="text-sm text-gray-500 mb-4">Upload a supplier CSV to create or update products for the currently selected store.</p>
      <div className="mb-4">
        <label htmlFor="csv-import" className="block text-sm font-medium text-gray-700">
          Upload CSV File
        </label>
        <input
          id="csv-import"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        <p className="mt-1 text-sm text-gray-500">
          CSV columns: Product Name, SKU, Barcode, Category, Subcategory, Retail Price, Cost Price, Stock Quantity, Description (optional)
        </p>
      </div>
      <div className="flex space-x-2">
        <button
          onClick={handleImport}
          disabled={importing || !file}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark disabled:bg-gray-400"
        >
          {importing ? 'Importing...' : 'Import Products'}
        </button>
        <button
          onClick={handleDownloadTemplate}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
        >
          Download Template
        </button>
      </div>

      {importing && (
        <div className="mt-4 w-full bg-gray-200 rounded-full">
          <div className="bg-blue-500 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full" style={{ width: '100%' }}>
            Processing...
          </div>
        </div>
      )}

      {error && <div className="mt-4 text-red-500">{error}</div>}

      {report && (
        <div className="mt-6">
          <h3 className="text-xl font-bold mb-2">Import Report</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-gray-100 rounded-lg">
              <p className="text-sm font-medium text-gray-500">Total Rows</p>
              <p className="text-2xl font-bold">{report.totalRows}</p>
            </div>
            <div className="p-4 bg-green-100 rounded-lg">
              <p className="text-sm font-medium text-green-600">Imported</p>
              <p className="text-2xl font-bold text-green-600">{report.successfulImports}</p>
            </div>
            <div className="p-4 bg-red-100 rounded-lg">
              <p className="text-sm font-medium text-red-600">Errors</p>
              <p className="text-2xl font-bold text-red-600">{report.errorCount}</p>
            </div>
          </div>

          {report.errors && report.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="font-bold">Errors:</h4>
              <ul className="list-disc list-inside text-red-500">
                {report.errors.map((err, index) => (
                  <li key={index}>
                    Row {index + 1}: {err.error} (SKU: {err.item.SKU})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.importedProducts && report.importedProducts.length > 0 && (
            <div className="mt-4">
              <h4 className="font-bold">Imported Products:</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 mt-2">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.importedProducts.map((prod, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{prod.SKU}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{prod['Product Name']}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {prod.status === 'Created' ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Created
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              Updated
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductImport;
